'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

export interface AdTrackingData {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landingPage?: string;
  timestamp?: string;
}

interface AdTrackingContextType {
  trackingData: AdTrackingData;
  updateTrackingData: (data: Partial<AdTrackingData>) => void;
  getTrackingData: () => AdTrackingData;
}

const AdTrackingContext = createContext<AdTrackingContextType | undefined>(undefined);

const STORAGE_KEY = 'ad_tracking_data';
const STORAGE_EXPIRY_DAYS = 30;

function readStored(): AdTrackingData {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as AdTrackingData) : {};
  } catch {
    return {};
  }
}

export function AdTrackingProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [trackingData, setTrackingData] = useState<AdTrackingData>({});

  // Load data from session storage on mount
  useEffect(() => {
    const loadStoredData = () => {
      if (typeof window === 'undefined') return;
      
      try {
        const storedData = sessionStorage.getItem(STORAGE_KEY);
        if (storedData) {
          const parsed = JSON.parse(storedData);
          const storedTimestamp = parsed.timestamp;
          
          // Check if data is not expired (30 days)
          if (storedTimestamp) {
            const expiryTime = new Date(storedTimestamp).getTime() + (STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
            if (new Date().getTime() > expiryTime) {
              sessionStorage.removeItem(STORAGE_KEY);
              return;
            }
          }
          
          setTrackingData(parsed);
        }
      } catch (error) {
        console.error('Error loading ad tracking data:', error);
      }
    };

    loadStoredData();
  }, []);

  // Extract and save tracking parameters on route change
  useEffect(() => {
    const extractTrackingParams = () => {
      // Read the URL after mount, not through useSearchParams: that hook sits above every
      // page and would turn each prerendered landing page into a client-side bailout (the
      // same reason useOpenDay reads window.location). Click IDs arrive on the landing
      // URL, which is exactly what this sees on first run.
      const searchParams = new URLSearchParams(window.location.search);
      // Build on what is stored, not on state: on a full reload state is still {} here,
      // and writing from it would wipe UTMs captured on an earlier page of this tab.
      const newData: AdTrackingData = { ...readStored(), ...trackingData };
      let hasNewData = false;

      // Google Ads parameters
      const gclid = searchParams.get('gclid');
      const gbraid = searchParams.get('gbraid');
      const wbraid = searchParams.get('wbraid');
      
      // Facebook parameters
      const fbclid = searchParams.get('fbclid');
      
      // UTM parameters
      const utm_source = searchParams.get('utm_source');
      const utm_medium = searchParams.get('utm_medium');
      const utm_campaign = searchParams.get('utm_campaign');
      const utm_term = searchParams.get('utm_term');
      const utm_content = searchParams.get('utm_content');

      // Update if new values are found
      if (gclid && gclid !== newData.gclid) {
        newData.gclid = gclid;
        hasNewData = true;
      }
      if (gbraid && gbraid !== newData.gbraid) {
        newData.gbraid = gbraid;
        hasNewData = true;
      }
      if (wbraid && wbraid !== newData.wbraid) {
        newData.wbraid = wbraid;
        hasNewData = true;
      }
      if (fbclid && fbclid !== newData.fbclid) {
        newData.fbclid = fbclid;
        hasNewData = true;
      }
      if (utm_source && utm_source !== newData.utm_source) {
        newData.utm_source = utm_source;
        hasNewData = true;
      }
      if (utm_medium && utm_medium !== newData.utm_medium) {
        newData.utm_medium = utm_medium;
        hasNewData = true;
      }
      if (utm_campaign && utm_campaign !== newData.utm_campaign) {
        newData.utm_campaign = utm_campaign;
        hasNewData = true;
      }
      if (utm_term && utm_term !== newData.utm_term) {
        newData.utm_term = utm_term;
        hasNewData = true;
      }
      if (utm_content && utm_content !== newData.utm_content) {
        newData.utm_content = utm_content;
        hasNewData = true;
      }

      // Get Facebook Click ID (fbc) from cookie if available
      if (typeof window !== 'undefined' && document.cookie) {
        const fbcMatch = document.cookie.match(/_fbc=([^;]+)/);
        if (fbcMatch && fbcMatch[1] && fbcMatch[1] !== newData.fbc) {
          newData.fbc = fbcMatch[1];
          hasNewData = true;
        }

        // Get Facebook Browser ID (fbp) from cookie
        const fbpMatch = document.cookie.match(/_fbp=([^;]+)/);
        if (fbpMatch && fbpMatch[1] && fbpMatch[1] !== newData.fbp) {
          newData.fbp = fbpMatch[1];
          hasNewData = true;
        }
      }

      // Capture referrer and landing page on first visit
      if (!newData.referrer && typeof window !== 'undefined' && document.referrer) {
        newData.referrer = document.referrer;
        hasNewData = true;
      }
      
      if (!newData.landingPage && pathname) {
        newData.landingPage = pathname;
        hasNewData = true;
      }

      // Update timestamp
      if (hasNewData) {
        newData.timestamp = new Date().toISOString();
        setTrackingData(newData);
        
        // Save to session storage
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
        } catch (error) {
          console.error('Error saving ad tracking data:', error);
        }
      }
    };

    extractTrackingParams();
  }, [pathname]); // Note: not including trackingData to avoid infinite loop

  const updateTrackingData = (data: Partial<AdTrackingData>) => {
    const newData = {
      ...trackingData,
      ...data,
      timestamp: new Date().toISOString(),
    };
    
    setTrackingData(newData);
    
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
    } catch (error) {
      console.error('Error updating ad tracking data:', error);
    }
  };

  const getTrackingData = () => {
    // Always return the latest data from session storage
    try {
      const storedData = sessionStorage.getItem(STORAGE_KEY);
      if (storedData) {
        return JSON.parse(storedData) as AdTrackingData;
      }
    } catch (error) {
      console.error('Error getting ad tracking data:', error);
    }
    return trackingData;
  };

  return (
    <AdTrackingContext.Provider value={{ trackingData, updateTrackingData, getTrackingData }}>
      {children}
    </AdTrackingContext.Provider>
  );
}

export function useAdTracking() {
  const context = useContext(AdTrackingContext);
  if (!context) {
    throw new Error('useAdTracking must be used within AdTrackingProvider');
  }
  return context;
}